import { ADynamicFormField } from '@acaprojects/ngx-dynamic-forms';
import { Validators } from '@angular/forms';

import { BaseAPIService } from '../base.service';
import { BaseDataClass } from '../base-api.class';
import { MapObject } from 'src/app/shared/utilities/types.utilities';
import { IChatMessage, ChatMessageGroup } from 'src/app/services/chat/chat.interfaces';
import { IUserType } from 'src/app/services/data/users/user.class';

import * as dayjs from 'dayjs';

export interface ISpaceAvailabilityOptions {
    date?: number;
    duration: number;
    id?: string;
    room_ids?: string;
    bookable?: boolean;
    rooms?: string;
    ignore?: string;
    zone_id?: string;
    clear?: boolean;
}

export interface IOpeningHour {
    available: boolean;
    open: string;
    close: string;
}

const OPTION_DEFAULTS: ISpaceAvailabilityOptions = {
    duration: 60,
};

export class Space extends BaseDataClass {
    /** Short name for the space */
    readonly short_name: string;
    /** CSS selector for the location of the space of the level's map */
    readonly map_id: string;
    /** Type of space */
    readonly type: string;
    /** Whether user can search for this space */
    readonly can_search: boolean;
    /** Whether user can open the control URL if available */
    readonly can_control: boolean;
    /** Hourly cost for using the space */
    readonly hourly_rate: number;
    /** Whether space can be booked by users */
    readonly bookable: boolean;
    /** People capacity of the space */
    readonly capacity: number;
    /** Index to force order when sorting multiple spaces */
    readonly sort_priority: number;
    /** Setup time for bookings in this space */
    readonly setup: number;
    /** Breakdown time for bookings in this space */
    readonly breakdown: number;
    /** Last returned availability result */
    readonly was_available: boolean;
    /** Longest waiting time for the room in minutes */
    readonly waiting_time: number;
    /** Current number of participants in the room */
    readonly participants: number;
    /** Number of active calls for the room */
    readonly calls: number;
    /** Show billing consent */
    readonly billing_consent: boolean;
    /** Description of space */
    readonly description: string;
    /** Whether space is available */
    readonly available: boolean;
    /** Organisation */
    readonly organisation: string;

    /** District Id associated with the space */
    readonly health_district_id: string;
    /** Is open all day */
    readonly open_24_7: boolean;
    /** List member names in patient page */
    readonly list_member_names: any[];
    /** Whether to send email */
    readonly send_admin_email: boolean;
    readonly send_coord_email: boolean;
    readonly send_clinic_email: boolean;
    readonly send_interp_email: boolean;
    /** Whether to notify */
    readonly notify_room_entry: boolean;
    readonly notify_every_5min: boolean;
    readonly notify_chat_message: boolean;
    /** Link for welcome */
    readonly welcome_video: string;
    /** Link for PREM exit survey */
    readonly exit_survey: string;
    /** Link for Relatives, Carers and Friends survey */
    readonly relative_carer_friend_survey: string;
    /** Link for Other Providers survey */
    readonly other_provider_survey: string;
    /** Link for NSW Health Employees survey */
    readonly employee_survey: string;
    /** Link for help */
    readonly help_button: string;
    /** Time of creation */
    readonly created_at: number;
    /** Opening hours object */
    readonly opening_hours: MapObject<IOpeningHour>;
    /** Pexip URL */
    readonly pexip_url: string;
    /** Chatbot messages for the space */
    readonly chatbot_messages: IChatMessage[];

    /** Level in which the spaces is associated */
    private _level: MapObject;
    /** Bookings associated with the space */
    private _bookings: string[];
    /** Engine zones associated with the space */
    private _zones: string[];
    /** List of equipment available in the space */
    private _extras: { id: string; name: string }[];
    /** List of ids for linked spaces */
    private _linked_spaces: string[];

    constructor(protected service: BaseAPIService<Space>, raw_data: MapObject = {}) {
        super(service, raw_data);
        if (!raw_data) {
            raw_data = {};
        }
        const settings = raw_data.settings || {};

        this.short_name = settings.short_name || raw_data.short_name;
        this.map_id = settings.map_id || raw_data.map_id;
        this._level = { settings: {} };
        this.type =
            settings.book_type ||
            this._level.book_type ||
            this._level.settings.book_type ||
            raw_data.book_type ||
            raw_data.type ||
            'book';
        this.can_search =
            settings.searchable || raw_data.searchable || raw_data.can_search || false;
        this.can_control =
            settings.controlable || raw_data.controlable || raw_data.can_control || false;
        this.hourly_rate = settings.cost_hour || raw_data.cost_hour || raw_data.hourly_rate || 0;
        this.bookable = settings.bookable || raw_data.bookable || false;
        this.setup = (settings.setup || 0) / 60 || raw_data.setup || 0;
        this.breakdown = (settings.breakdown || 0) / 60 || raw_data.breakdown || 0;
        this.capacity = raw_data.capacity || 0;
        this.was_available =
            settings.available || raw_data.available || raw_data.was_available || false;
        this.calls = raw_data.calls;
        this.waiting_time = raw_data.waiting_time;
        this.participants = raw_data.participants;
        this.billing_consent = raw_data.billing_consent;
        this.description = raw_data.description;
        this.available = raw_data.available;
        this.organisation = raw_data.organisation;

        this.health_district_id = raw_data.health_district_id;
        this.open_24_7 = raw_data.open_24_7 || false;
        this.list_member_names = raw_data.list_member_names;
        this.notify_room_entry = raw_data.notify_room_entry;
        this.notify_every_5min = raw_data.notify_every_5min;
        this.notify_chat_message = raw_data.notify_chat_message;
        this.send_admin_email = raw_data.send_admin_email;
        this.send_coord_email = raw_data.send_coord_email;
        this.send_clinic_email = raw_data.send_clinic_email;
        this.send_interp_email = raw_data.send_interp_email;

        this.welcome_video = raw_data.welcome_video;
        this.exit_survey = raw_data.exit_survey;
        this.relative_carer_friend_survey = raw_data.relative_carer_friend_survey;
        this.other_provider_survey = raw_data.other_provider_survey;
        this.employee_survey = raw_data.employee_survey;
        this.help_button = raw_data.help_button;
        this.created_at = dayjs(raw_data.created_at * 1000).valueOf();
        this.opening_hours = raw_data.opening_hours;
        this.pexip_url = raw_data.pexip_url;
        this.chatbot_messages = raw_data.chatbot_messages || [];

        this._zones = raw_data.zones || [];
        const raw_bookings = settings.bookings || raw_data.bookings || [];
        this._bookings = raw_bookings.map((i) => i.id);
        this._extras = [];
        this._linked_spaces =
            settings.linked_rooms || raw_data.linked_rooms || raw_data.linked_spaces || [];
    }

    /** List of ids for linked spaces */
    public get linked_spaces() {
        return [...this._linked_spaces];
    }

    /** Level in which the space is associated */
    public get level(): MapObject {
        return this._level;
    }

    /** Engine zones associated with the space */
    public get zones(): string[] {
        return [...this._zones];
    }

    /**
     * Whether the space is available.
     */
    public isAvailable(options?: ISpaceAvailabilityOptions): Promise<boolean> {
        options = options
            ? { ...OPTION_DEFAULTS, ...options, room_ids: this.id }
            : { ...OPTION_DEFAULTS, room_ids: this.id };
        return new Promise((resolve) => {
            (this.service as any).available(options).then(
                (list: Space[]) => {
                    for (const rm of list) {
                        if (rm.id === this.id) {
                            return resolve(true);
                        }
                    }
                    resolve(false);
                },
                () => resolve(false)
            );
        });
    }

    /**
     * Make a copy of this object
     */
    public clone(): Space {
        return new Space(this.service, this);
    }

    /**
     * Make a copy of this object without identification data
     */
    public duplicate(): Space {
        return new Space(this.service, { ...this, id: null, email: null, _bookings: [] });
    }

    public userExitSurvey(user_type: IUserType): string {
        const exit_survey = {
            carer: this.relative_carer_friend_survey,
            'another provider': this.other_provider_survey,
            employee: this.employee_survey,
        }[user_type];

        return exit_survey || this.exit_survey;
    }

    /** Messages for a group */
    public chatbotGroupMessages(group: ChatMessageGroup): IChatMessage[] {
        return this.chatbot_messages.filter((message) => {
            return message.enabled && !!message.group.match(`all|${group}`);
        });
    }

    /** System messages for a group */
    public chatbotSystemMessages(group: ChatMessageGroup): IChatMessage[] {
        return this.chatbotGroupMessages(group).filter((message) => {
            return message.type === 'notice' && message.key !== 'file-notice';
        });
    }

    /** Standard messages for a group */
    public chatbotStandardMessages(group: ChatMessageGroup): IChatMessage[] {
        return this.chatbotGroupMessages(group).filter((message) => {
            return message.type !== 'notice' && message.key !== 'file-notice';
        });
    }

    /** File upload message */
    public chatbotFileUploadMessage(): Partial<IChatMessage> | undefined {
        return {
            enabled: false,
            key: 'file-notice',
            group: 'all',
            type: 'notice',
            style: 'alert',
            text: 'You will need to download attachments shared before the call ends.',
            delay: 0,
        };
    }

    /**
     * Initialise the form field metadata for the field
     * TODO: shouldn't be in the patient interface check if it can be removed
     */
    protected initialiseFormFields(): ADynamicFormField[] {
        const edit = !!this.id;
        const fields: ADynamicFormField[] = [
            {
                key: 'name',
                type: 'input',
                value: this.name,
                required: true,
            },
            {
                key: 'short_name',
                type: 'input',
                value: this.short_name,
                required: true,
                validators: [Validators.maxLength(12)],
            },
            {
                key: 'billing_consent',
                type: 'checkbox',
                value: this.billing_consent,
                required: true,
            },
            {
                key: 'health_district_id',
                type: 'custom',
                value: this.health_district_id,
                required: true,
            },
            {
                key: 'description',
                type: 'textarea',
                value: this.description,
            },
            {
                key: 'available',
                type: 'checkbox',
                value: this.available,
                required: true,
            },
            {
                key: 'open_24_7',
                type: 'checkbox',
                value: this.open_24_7,
            },
            {
                key: 'list_member_names',
                type: 'checkbox',
                value: this.list_member_names,
            },
            {
                key: 'welcome_video',
                type: 'input',
                value: this.welcome_video,
            },
            {
                key: 'help_button',
                type: 'input',
                value: this.help_button,
            },
            {
                key: 'exit_survey',
                type: 'input',
                value: this.exit_survey,
            },
            {
                key: 'send_admin_email',
                type: 'checkbox',
                value: this.send_admin_email,
            },
            {
                key: 'send_coord_email',
                type: 'checkbox',
                value: this.send_coord_email,
            },
            {
                key: 'send_clinic_email',
                type: 'checkbox',
                value: this.send_clinic_email,
            },
            {
                key: 'send_interp_email',
                type: 'checkbox',
                value: this.send_interp_email,
            },
            {
                key: 'notify_room_entry',
                type: 'checkbox',
                value: this.notify_room_entry,
            },
            {
                key: 'notify_every_5min',
                type: 'checkbox',
                value: this.notify_every_5min,
            },
            {
                key: 'notify_chat_message',
                type: 'checkbox',
                value: this.notify_chat_message,
            },
            {
                key: 'opening_hours',
                type: 'custom',
                value: this.opening_hours,
            },
        ].map((i) => new ADynamicFormField(i as any));
        return fields;
    }
}
